import {IProgress, isNullOrEmpty} from "@2d-front-monorepo/core";
import {JSX, ReactNode, useEffect, useRef, useState} from "react";

type LoadingDialogData = {
	visible: boolean;
} & IProgress;

type LoadingProps = {
	children?: ReactNode;
	label: string;
	delay?: number;
}

export function toggleLoadingDialog(visible: boolean, data?: IProgress): void {
	window.dispatchEvent(new CustomEvent('toggle-loading', {
		detail: {
			visible,
			total: data?.total ?? 0,
			current: data?.current ?? 0,
			label: data?.label ?? ""
		}
	}));
}

function showModal(dialog: React.RefObject<HTMLDialogElement | null>): void {
	if (!dialog.current) {
		return;
	}

	dialog.current.showModal();
}

export default function Loading({children, label, delay = 0}: LoadingProps) {
	const ref = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		if (delay > 0) {
			const timer = setTimeout(() => {
				showModal(ref);
			}, delay);

			return () => clearTimeout(timer);

		} else {
			showModal(ref);
		}

		return undefined;
	}, [delay]);

	return <dialog role="progressbar" title={label} aria-busy="true" aria-live="assertive" ref={ref}>{children}</dialog>;
};

function Progress({props}: { props: LoadingDialogData }): JSX.Element {
	return <div className="progress-indicator">
		<label htmlFor="progress-bar">{props.label}</label>
		<progress id="progress-bar"
		          max={props.total < 0 ? undefined : props.total}
		          value={props.current < 0 ? undefined : props.current}/>
	</div>;
}

export function LoadingDialog({children}: { children?: ReactNode }): JSX.Element {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [data, setData] = useState<LoadingDialogData>({label: "", visible: false, current: -1, total: -1});

	useEffect(() => {
		const handleToggle = (e: Event) => {
			const dialog = dialogRef.current;
			if (!dialog) {
				return;
			}

			const data = (e as CustomEvent<LoadingDialogData>).detail;
			if (data.visible && !dialog.open) {
				dialog.showModal();
				dialog.focus();
			}

			if (!data.visible && dialog.open) {
				dialog.close();
			}
			setData(data);
		};

		window.addEventListener('toggle-loading', handleToggle);

		return () => {
			window.removeEventListener('toggle-loading', handleToggle);
		}
	}, [data]);

	return <dialog ref={dialogRef} role="progressbar">
		{isNullOrEmpty(data.label) ? children : <Progress props={data}/>}
	</dialog>;
}
